import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { CloudSun, Navigation, Loader2, ChevronDown, Maximize2, X, ArrowLeftRight } from 'lucide-react';
import type { GoogleCoordinate, GeocodingResult } from '@/types';

// Komponenter
import DateTimePicker, { getDefaultDateTime } from '@/components/DateTimePicker/DateTimePicker';
import { LocationAutocomplete } from '@/components/LocationAutocomplete/LocationAutocomplete';
import { WeatherTimeline } from '@/components/WeatherTimeline/WeatherTimeline';
import { WarningBanner } from '@/components/WarningBanner/WarningBanner';
import { WeatherCard } from '@/components/WeatherCard/WeatherCard';
import { CookieBanner } from '@/components/CookieBanner/CookieBanner';

// Hooks
import { useRouteWeather } from '@/hooks/useRouteWeather';

// Services
import { getWeatherEmoji } from '@/services/smhi';
import { getGoogleMapsApiKey, toGoogleLatLng } from '@/services/googlemaps';

// Sverige-centrerade koordinater
const SWEDEN_CENTER: GoogleCoordinate = { lat: 62.0, lng: 15.0 };
const SWEDEN_ZOOM = 5;

// Google Maps container style
const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

// Google Maps options
const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
};

/**
 * Formatera tid (t.ex. "6h 15min")
 */
function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours === 0) {
    return `${minutes} min`;
  }
  return `${hours}h ${minutes}min`;
}

/**
 * Formatera avstånd (t.ex. "614 km")
 */
function formatDistance(meters: number): string {
  const km = Math.round(meters / 1000);
  return `${km} km`;
}

/**
 * Weather Route Planner - Huvudapplikation
 */
export default function App() {
  const [origin, setOrigin] = useState<GeocodingResult | null>(null);
  const [destination, setDestination] = useState<GeocodingResult | null>(null);
  const [departureTime, setDepartureTime] = useState<Date>(getDefaultDateTime());
  const [showDetailedWeather, setShowDetailedWeather] = useState(false);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<number | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);

  const { route, alternatives, selectedRouteIndex, isLoading, error, search, selectRoute, warnings } = useRouteWeather({
    origin,
    destination,
    departureTime,
  });

  // Ref för att hålla koll på polylines så vi kan ta bort dem
  const polylinesRef = useRef<google.maps.Polyline[]>([]);

  // Hantera polylines manuellt via Google Maps API
  useEffect(() => {
    if (!map) return;

    // Ta bort alla gamla polylines
    polylinesRef.current.forEach(polyline => {
      polyline.setMap(null);
    });
    polylinesRef.current = [];

    // Skapa nya polylines för varje rutt
    alternatives.forEach((alt, index) => {
      const isSelected = index === selectedRouteIndex;

      const polyline = new google.maps.Polyline({
        path: alt.polyline,
        strokeColor: isSelected ? '#3B82F6' : '#9CA3AF',
        strokeWeight: isSelected ? 5 : 6,
        strokeOpacity: isSelected ? 0.9 : 0.5,
        zIndex: isSelected ? 2 : 1,
        clickable: !isSelected,
        map: map,
      });

      // Lägg till click handler för alternativa rutter
      if (!isSelected) {
        polyline.addListener('click', () => {
          selectRoute(index);
        });
      }

      polylinesRef.current.push(polyline);
    });

    // Cleanup när komponenten unmountas
    return () => {
      polylinesRef.current.forEach(polyline => {
        polyline.setMap(null);
      });
      polylinesRef.current = [];
    };
  }, [map, alternatives, selectedRouteIndex, selectRoute]);

  const handleSearch = async () => {
    if (!origin || !destination) return;
    await search();
  };

  // Byt plats på start och destination (inga nya API-anrop förrän användaren söker)
  const handleSwapLocations = () => {
    const tempOrigin = origin;
    setOrigin(destination);
    setDestination(tempOrigin);
  };

  // Fit bounds when route changes
  const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);

  // Fit map to route bounds when route changes
  useMemo(() => {
    if (map && route && route.polyline.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      route.polyline.forEach(coord => {
        bounds.extend(coord);
      });
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }
  }, [map, route]);

  // Beräkna start- och destinationstemperatur
  const temperatureRange = useMemo(() => {
    if (!route || route.weatherPoints.length === 0) return null;

    const firstPoint = route.weatherPoints[0];
    const lastPoint = route.weatherPoints[route.weatherPoints.length - 1];
    if (!firstPoint || !lastPoint) return null;

    return {
      start: Math.round(firstPoint.weather.temperature),
      end: Math.round(lastPoint.weather.temperature),
    };
  }, [route]);

  // Räkna varningar längs rutten
  const routeWarnings = useMemo(() => {
    if (!route) return [];

    const warningIds = new Set<string>();
    route.weatherPoints.forEach(p => {
      p.warnings.forEach(w => warningIds.add(w.id));
    });

    return warnings.filter(w => warningIds.has(w.id));
  }, [route, warnings]);

  return (
    <LoadScript googleMapsApiKey={getGoogleMapsApiKey()}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-sm shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <img src="/ikon.png" alt="Färdväder" className="w-24 h-24 rounded-lg shadow-lg object-cover" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Färdväder</h1>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-6">
          {/* Varningsbanner */}
          {routeWarnings.length > 0 && (
            <div className="mb-6">
              <WarningBanner warnings={routeWarnings} />
            </div>
          )}

          {/* Sökformulär - full bredd överst */}
          <div className="card animate-fade-in mb-6 relative z-[200]">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Navigation className="w-5 h-5 text-blue-500" />
              Planera din resa
            </h2>

            <div className="grid md:grid-cols-4 gap-4 items-end">
              {/* Från och Till med swap-knapp */}
              <div className="md:col-span-2 flex flex-col md:flex-row gap-2 md:gap-4 items-end">
                <div className="flex-1 w-full">
                  <LocationAutocomplete
                    value={origin}
                    onChange={setOrigin}
                    placeholder="Varifrån? (t.ex. Stockholm)"
                    label="Startpunkt"
                    indicatorColor="green"
                  />
                </div>

                {/* Swap-knapp */}
                <button
                  type="button"
                  onClick={handleSwapLocations}
                  disabled={!origin && !destination}
                  className="p-2 mb-1 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-end"
                  aria-label="Byt plats på start och destination"
                  title="Byt plats"
                >
                  <ArrowLeftRight className="w-5 h-5 text-gray-600" />
                </button>

                <div className="flex-1 w-full">
                  <LocationAutocomplete
                    value={destination}
                    onChange={setDestination}
                    placeholder="Vart ska du? (t.ex. Malmö)"
                    label="Destination"
                    indicatorColor="red"
                  />
                </div>
              </div>

              {/* Avresetid */}
              <DateTimePicker
                value={departureTime}
                onChange={setDepartureTime}
              />

              {/* Sökknapp */}
              <button
                onClick={handleSearch}
                disabled={!origin || !destination || isLoading}
                className="btn-primary flex items-center justify-center gap-2 h-[42px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Beräknar...</span>
                  </>
                ) : (
                  <>
                    <CloudSun className="w-5 h-5" />
                    Sök
                  </>
                )}
              </button>
            </div>

            {/* Felmeddelande */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error.message}
              </div>
            )}
          </div>

          {/* Karta - full bredd */}
          <div className="space-y-6">
            {/* Karta */}
            <div className={`${isMapFullscreen ? 'fixed inset-0 z-[9999] p-0 rounded-none h-screen' : 'card p-2 h-[500px] lg:h-[700px] relative z-[1]'}`}>
              {/* Fullskärmsknapp för mobil */}
              <button
                onClick={() => setIsMapFullscreen(!isMapFullscreen)}
                className={`absolute z-[100] bg-white shadow-lg rounded-lg p-2 hover:bg-gray-100 transition-colors ${isMapFullscreen ? 'top-4 right-4' : 'top-4 right-4'}`}
                aria-label={isMapFullscreen ? 'Stäng fullskärm' : 'Visa i fullskärm'}
              >
                {isMapFullscreen ? (
                  <X className="w-6 h-6 text-gray-700" />
                ) : (
                  <Maximize2 className="w-6 h-6 text-gray-700" />
                )}
              </button>
              <div className={`h-full w-full ${isMapFullscreen ? '' : 'rounded-xl'} overflow-hidden`}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={SWEDEN_CENTER}
                  zoom={SWEDEN_ZOOM}
                  options={mapOptions}
                  onLoad={onMapLoad}
                >
                  {/* Polylines hanteras via useEffect för korrekt uppdatering */}

                  {/* Vädermarkörer */}
                  {route && route.weatherPoints.map((point, index) => {
                        const isFirst = index === 0;
                        const isLast = index === route.weatherPoints.length - 1;
                        const position = toGoogleLatLng(point.location);

                        // Bestäm varningsklass
                        const hasWarning = point.warnings.length > 0;
                        const warningLevel = hasWarning
                          ? point.warnings.some(w => w.level === 'RED') ? 'red'
                          : point.warnings.some(w => w.level === 'ORANGE') ? 'orange'
                          : 'yellow'
                          : null;

                        const emoji = isFirst ? '🚗' : isLast ? '🏁' : getWeatherEmoji(point.weather.weatherSymbol, point.location, point.estimatedArrival);

                        return (
                          <Marker
                            key={`${point.location.lat}-${point.location.lon}`}
                            position={position}
                            icon={{
                              url: `data:image/svg+xml,${encodeURIComponent(`
                                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                                  <circle cx="20" cy="20" r="18" fill="${isFirst ? '#22c55e' : isLast ? '#ef4444' : '#ffffff'}" stroke="${warningLevel === 'red' ? '#ef4444' : warningLevel === 'orange' ? '#f97316' : warningLevel === 'yellow' ? '#eab308' : '#e5e7eb'}" stroke-width="3"/>
                                  <text x="20" y="26" text-anchor="middle" font-size="16">${emoji}</text>
                                </svg>
                              `)}`,
                              scaledSize: new google.maps.Size(40, 40),
                              anchor: new google.maps.Point(20, 20),
                            }}
                            onClick={() => setSelectedMarkerIndex(index)}
                          />
                        );
                      })}

                  {/* InfoWindow för vald markör */}
                  {route && selectedMarkerIndex !== null && route.weatherPoints[selectedMarkerIndex] && (
                    <InfoWindow
                      position={toGoogleLatLng(route.weatherPoints[selectedMarkerIndex].location)}
                      onCloseClick={() => setSelectedMarkerIndex(null)}
                    >
                      <div className="text-center p-2">
                        <div className="text-3xl mb-2">
                          {getWeatherEmoji(
                            route.weatherPoints[selectedMarkerIndex].weather.weatherSymbol,
                            route.weatherPoints[selectedMarkerIndex].location,
                            route.weatherPoints[selectedMarkerIndex].estimatedArrival
                          )}
                        </div>
                        <div className="font-semibold text-lg">
                          {Math.round(route.weatherPoints[selectedMarkerIndex].weather.temperature)}°C
                        </div>
                        <div className="text-sm text-gray-600">
                          {route.weatherPoints[selectedMarkerIndex].estimatedArrival.toLocaleTimeString('sv-SE', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        {route.weatherPoints[selectedMarkerIndex].warnings.length > 0 && (
                          <div className="mt-2 text-xs text-orange-600">
                            ⚠️ {route.weatherPoints[selectedMarkerIndex].warnings.length} varning(ar)
                          </div>
                        )}
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              </div>
            </div>

            {/* Ruttsammanfattning - direkt under kartan */}
            {route && (
              <div className="card animate-slide-up relative z-[10]">
                <h3 className="font-semibold text-gray-900 mb-3">Ruttsammanfattning</h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl mb-1">📏</div>
                    <div className="text-lg font-bold text-gray-900">
                      {formatDistance(route.totalDistance)}
                    </div>
                    <div className="text-xs text-gray-500">Sträcka</div>
                  </div>

                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl mb-1">⏱️</div>
                    <div className="text-lg font-bold text-gray-900">
                      {formatDuration(route.totalDuration)}
                    </div>
                    <div className="text-xs text-gray-500">Restid</div>
                  </div>

                  {temperatureRange && (
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl mb-1">🌡️</div>
                      <div className="text-lg font-bold text-gray-900">
                        {temperatureRange.start}° → {temperatureRange.end}°
                      </div>
                      <div className="text-xs text-gray-500">Start → Mål</div>
                    </div>
                  )}

                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl mb-1">⚠️</div>
                    <div className="text-lg font-bold">
                      {routeWarnings.length === 0 ? (
                        <span className="text-green-600">0</span>
                      ) : (
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-white font-bold ${
                          routeWarnings.some(w => w.level === 'RED') ? 'bg-red-500' :
                          routeWarnings.some(w => w.level === 'ORANGE') ? 'bg-orange-500' :
                          'bg-yellow-500'
                        }`}>
                          {routeWarnings.length}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">Varningar (SE)</div>
                  </div>
                </div>

                {/* Ankomsttid */}
                <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-cyan-50 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm text-gray-600">Beräknad ankomst:</span>
                    <span className="font-bold text-gray-900">
                      {route.arrivalTime.toLocaleTimeString('sv-SE', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {' '}
                      ({route.arrivalTime.toLocaleDateString('sv-SE', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      })})
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Vädertidslinje med expanderbara detaljer */}
            {route && route.weatherPoints.length > 0 && (
              <div className="card">
                <button
                  onClick={() => setShowDetailedWeather(!showDetailedWeather)}
                  className="w-full flex items-center justify-between text-left"
                  aria-expanded={showDetailedWeather}
                >
                  <h3 className="font-semibold text-gray-900">
                    Väder längs rutten
                  </h3>
                  <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors flex items-center gap-2 cursor-pointer">
                    {showDetailedWeather ? (
                      <>
                        <ChevronDown className="w-4 h-4 rotate-180 transition-transform duration-200" />
                        Dölj detaljer
                      </>
                    ) : (
                      <>
                        <span className="text-xs font-bold bg-blue-200 px-1.5 py-0.5 rounded-full">
                          {route.weatherPoints.length}
                        </span>
                        Visa detaljer
                        <ChevronDown className="w-4 h-4 transition-transform duration-200" />
                      </>
                    )}
                  </span>
                </button>

                <div className="mt-4">
                  <WeatherTimeline
                    weatherPoints={route.weatherPoints}
                    orientation="horizontal"
                  />
                </div>

                {/* Detaljerade väderkort - expanderbar */}
                {showDetailedWeather && (
                  <div className="mt-6 pt-6 border-t border-gray-100 animate-slide-up">
                    <h4 className="font-medium text-gray-700 mb-4">Detaljerad väderinfo</h4>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {route.weatherPoints.map((point, index) => (
                        <WeatherCard
                          key={`card-${point.location.lat}-${point.location.lon}`}
                          weatherPoint={point}
                          isFirst={index === 0}
                          isLast={index === route.weatherPoints.length - 1}
                          index={index}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </main>

        {/* Footer */}
        <footer className="mt-8 py-6 bg-white/50 border-t">
          <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
            <p>
              Kartor & Routing: <a href="https://cloud.google.com/maps-platform" className="underline hover:text-blue-600">Google Maps</a>
              {' • '}
              Väderdata: <a href="https://www.smhi.se/" className="underline hover:text-blue-600">SMHI</a> (CC BY 4.0)
            </p>
          </div>
        </footer>

        {/* Cookie-banner */}
        <CookieBanner />
      </div>
    </LoadScript>
  );
}
